import { useEffect, useRef, type RefObject } from "react";

const SCROLL_EPS_PX = 0.5;
const SYNC_GUARD_MS = 64;
const PROGRAMMATIC_GUARD_MS = 80;
const LANE_SCROLL_WRITE_EPS_PX = 0.5;

/**
 * Mantiene pentagrama y tablatura/mástil con el mismo desplazamiento horizontal
 * cuando el usuario hace scroll manual en uno de los carriles.
 */
export function useLinkedLaneScroll(
  staffRef: RefObject<HTMLDivElement | null>,
  highwayRef: RefObject<HTMLDivElement | null>,
  /** `highway.scrollLeft - staff.scrollLeft` tras el último scroll programático (playback). */
  laneScrollDeltaRef: RefObject<number>,
  /** Marca scroll programático (playback) para no reaccionar en los listeners. */
  programmaticScrollRef: RefObject<boolean>,
  /** Cambia cuando los carriles se montan (p. ej. al cargar preparedSong). */
  attachKey: unknown = true,
  /** Timestamp (performance.now) hasta el cual ignorar scroll manual. */
  programmaticScrollUntilRef?: RefObject<number>
) {
  const syncingUntilRef = useRef(0);

  useEffect(() => {
    const staff = staffRef.current;
    const highway = highwayRef.current;
    if (!staff || !highway) return;

    laneScrollDeltaRef.current = highway.scrollLeft - staff.scrollLeft;

    const isProgrammatic = () =>
      programmaticScrollRef.current ||
      performance.now() < (programmaticScrollUntilRef?.current ?? 0) ||
      performance.now() < syncingUntilRef.current;

    const refreshDelta = () => {
      laneScrollDeltaRef.current = highway.scrollLeft - staff.scrollLeft;
    };

    const syncHighwayToStaff = () => {
      const want = staff.scrollLeft + (laneScrollDeltaRef.current ?? 0);
      if (Math.abs(highway.scrollLeft - want) <= SCROLL_EPS_PX) return;
      syncingUntilRef.current = performance.now() + SYNC_GUARD_MS;
      highway.scrollLeft = want;
      refreshDelta();
    };

    const syncStaffToHighway = () => {
      const want = highway.scrollLeft - (laneScrollDeltaRef.current ?? 0);
      if (Math.abs(staff.scrollLeft - want) <= SCROLL_EPS_PX) return;
      syncingUntilRef.current = performance.now() + SYNC_GUARD_MS;
      staff.scrollLeft = want;
      refreshDelta();
    };

    let syncRaf = 0;
    let pendingSource: "staff" | "highway" | null = null;

    const flushSync = () => {
      syncRaf = 0;
      const src = pendingSource;
      pendingSource = null;
      if (!src || isProgrammatic()) return;
      if (src === "staff") syncHighwayToStaff();
      else syncStaffToHighway();
    };

    const scheduleSync = (source: "staff" | "highway") => {
      pendingSource = source;
      if (syncRaf) return;
      syncRaf = requestAnimationFrame(flushSync);
    };

    const onStaffScroll = () => {
      if (isProgrammatic()) return;
      scheduleSync("staff");
    };

    const onHighwayScroll = () => {
      if (isProgrammatic()) return;
      scheduleSync("highway");
    };

    staff.addEventListener("scroll", onStaffScroll, { passive: true });
    highway.addEventListener("scroll", onHighwayScroll, { passive: true });
    return () => {
      if (syncRaf) cancelAnimationFrame(syncRaf);
      staff.removeEventListener("scroll", onStaffScroll);
      highway.removeEventListener("scroll", onHighwayScroll);
    };
  }, [staffRef, highwayRef, laneScrollDeltaRef, programmaticScrollRef, programmaticScrollUntilRef, attachKey]);
}

/** Escribe scrollLeft en ambos carriles solo si el delta supera el umbral (menos repintados). */
export function applyLaneScrollPositions(
  staffEl: HTMLDivElement | null,
  highwayEl: HTMLDivElement | null,
  staffLeft: number,
  highwayLeft: number,
  laneScrollDeltaRef: RefObject<number>,
  programmaticScrollRef: RefObject<boolean>,
  programmaticScrollUntilRef: RefObject<number>
): void {
  laneScrollDeltaRef.current = highwayLeft - staffLeft;
  armProgrammaticLaneScroll(programmaticScrollRef, programmaticScrollUntilRef);
  if (staffEl) {
    const cur = staffEl.scrollLeft;
    if (Math.abs(cur - staffLeft) > LANE_SCROLL_WRITE_EPS_PX) staffEl.scrollLeft = staffLeft;
  }
  if (highwayEl) {
    const cur = highwayEl.scrollLeft;
    if (Math.abs(cur - highwayLeft) > LANE_SCROLL_WRITE_EPS_PX) highwayEl.scrollLeft = highwayLeft;
  }
}

/** Marca scroll programático en ambos refs durante `PROGRAMMATIC_GUARD_MS`. */
export function armProgrammaticLaneScroll(
  programmaticScrollRef: RefObject<boolean>,
  programmaticScrollUntilRef: RefObject<number>
): void {
  programmaticScrollRef.current = true;
  programmaticScrollUntilRef.current = performance.now() + PROGRAMMATIC_GUARD_MS;
  window.setTimeout(() => {
    programmaticScrollRef.current = false;
  }, PROGRAMMATIC_GUARD_MS);
}
